import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch, FaChevronDown, FaTimes } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { getAccountDeletionRequestsList, updateAccountDeletionRequestStatus } from '../services/subscriberWalletService';
import { formatDateTimeIST } from '../utils/dateFormatter';

const REASON_LABELS = {
  no_longer_using: 'No longer using the service/platform',
  found_better_alternative: 'Found a better alternative',
  privacy_concerns: 'Privacy concerns',
  too_many_notifications: 'Too many emails/notifications',
  difficulty_navigating: 'Difficulty navigating the platform',
  account_security_concerns: 'Account security concerns',
  personal_reasons: 'Personal reasons',
};

const STATUS_OPTIONS = [
  { value: 'initiated', label: 'Initiated' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_COLORS = {
  initiated: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PAGE_SIZE = 25;

const AccountDeletionRequestsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);

  // Status filter
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  });
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  // Update status modal
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  const lastUrlUpdateRef = useRef({ page: null, search: null, status: null });
  const lastApiCallRef = useRef({ page: null, search: null, status: null, role: null });
  const isLoadingRef = useRef(false);

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support',
    };
    return roleMapping[authRole] || null;
  })();

  const canAccess = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  // Sync URL
  useEffect(() => {
    const newPage = page > 1 ? page.toString() : null;
    const newSearch = search.trim() || null;
    const newStatus = statusFilter || null;

    if (
      lastUrlUpdateRef.current.page !== newPage ||
      lastUrlUpdateRef.current.search !== newSearch ||
      lastUrlUpdateRef.current.status !== newStatus
    ) {
      const params = new URLSearchParams();
      if (newPage) params.set('page', newPage);
      if (newSearch) params.set('search', newSearch);
      if (newStatus) params.set('status', newStatus);
      setSearchParams(params, { replace: true });
      lastUrlUpdateRef.current = { page: newPage, search: newSearch, status: newStatus };
    }
  }, [page, search, statusFilter, setSearchParams]);

  // Debounce search
  const handleSearchSubmit = useCallback(() => {
    const trimmed = searchInput.trim();
    if (trimmed !== search) {
      setSearch(trimmed);
      setPage(1);
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, [searchInput, search]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(handleSearchSubmit, 2000);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchInput, handleSearchSubmit]);

  // Load data
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !currentRole) return;

    const currentPage = page || 1;
    const currentSearch = search.trim() || '';
    const currentStatus = statusFilter || '';

    if (
      lastApiCallRef.current.page === currentPage &&
      lastApiCallRef.current.search === currentSearch &&
      lastApiCallRef.current.status === currentStatus &&
      lastApiCallRef.current.role === currentRole
    ) return;

    if (isLoadingRef.current) return;

    lastApiCallRef.current = { page: currentPage, search: currentSearch, status: currentStatus, role: currentRole };

    const loadData = async () => {
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) { navigate('/login'); return; }

        const params = { page: currentPage };
        if (currentSearch) params.search = currentSearch;
        if (currentStatus) params.status = currentStatus;

        const response = await getAccountDeletionRequestsList(currentRole, params);

        let items = null;
        let paginationData = null;

        if (response?.msg?.items) {
          items = response.msg.items;
          paginationData = response.msg.pagination;
        } else if (response?.items) {
          items = response.items;
          paginationData = response.pagination;
        }

        if (Array.isArray(items)) {
          setRequests(items);
          if (paginationData) {
            setPagination({
              ...paginationData,
              total_pages: Math.ceil(paginationData.total / paginationData.items),
            });
          }
        } else {
          setRequests([]);
        }
      } catch (err) {
        setError('Failed to load account deletion requests. Please try again.');
        setRequests([]);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadData();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search, statusFilter]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.status-dropdown-container')) setStatusDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(prev => prev === value ? '' : value);
    setPage(1);
    setStatusDropdownOpen(false);
  };

  const openUpdateModal = (req) => {
    setSelectedRequest(req);
    setNewStatus(req.status);
    setNotes('');
    setUpdateError('');
  };

  const closeUpdateModal = () => {
    setSelectedRequest(null);
    setNewStatus('');
    setNotes('');
    setUpdateError('');
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    setUpdating(true);
    setUpdateError('');
    try {
      await updateAccountDeletionRequestStatus(currentRole, selectedRequest.id, newStatus, notes);
      // Refresh list
      lastApiCallRef.current = { page: null, search: null, status: null, role: null };
      closeUpdateModal();
      setLoading(true);
      const params = { page };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const response = await getAccountDeletionRequestsList(currentRole, params);
      if (response?.msg?.items) setRequests(response.msg.items);
      else if (response?.items) setRequests(response.items);
    } catch (err) {
      setUpdateError('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
      setLoading(false);
    }
  };

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This feature is only available for Ops and SuperAdmin roles.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          {/* Header + Search + Filter */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Account Deletion Requests</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name, Mobile, or REF CODE..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchSubmit(); } }}
                />
              </div>

              {/* Status filter */}
              <div className="status-dropdown-container relative w-48">
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="w-full px-4 py-1.5 text-left bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 flex items-center justify-between text-sm"
                >
                  <span className="text-gray-700">
                    {statusFilter ? STATUS_OPTIONS.find(o => o.value === statusFilter)?.label : 'All Status'}
                  </span>
                  <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {statusDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                    <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        checked={statusFilter === ''}
                        onChange={() => handleStatusFilterChange('')}
                        className="mr-3 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">All Status</span>
                    </label>
                    {STATUS_OPTIONS.map(opt => (
                      <label key={opt.value} className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          checked={statusFilter === opt.value}
                          onChange={() => handleStatusFilterChange(opt.value)}
                          className="mr-3 h-4 w-4 text-blue-600 border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Clear filter tag */}
              {statusFilter && (
                <button
                  onClick={() => { setStatusFilter(''); setPage(1); }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200"
                >
                  {STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}
                  <FaTimes className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-6 py-4 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <>
              {requests.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <p className="mb-2">No account deletion requests found.</p>
                  <p className="text-sm text-gray-400">
                    {search.trim() || statusFilter ? 'Try adjusting your search or filter.' : 'No requests submitted yet.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 rounded-md min-w-max">
                      <thead className="bg-gray-50">
                        <tr className="border-b border-gray-200">
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Subscriber</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Mobile</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">REF CODE</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Reason</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Notes</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Requested At</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Processed By</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requests.map((req) => (
                          <tr key={req.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{req.subscriber_name || 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{req.subscriber_mobile || 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 font-mono text-xs text-gray-900 whitespace-nowrap">{req.ref_code || 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 max-w-xs truncate">{REASON_LABELS[req.reason] || req.reason || 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-800'}`}>
                                {STATUS_OPTIONS.find(o => o.value === req.status)?.label || req.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-500 max-w-xs">
                              {req.notes ? (
                                <span className="block truncate" title={req.notes}>{req.notes}</span>
                              ) : '—'}
                            </td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{req.created_at ? formatDateTimeIST(req.created_at) : 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{req.processed_by || '—'}</td>
                            <td className="px-3 sm:px-6 py-2 whitespace-nowrap">
                              {req.status !== 'completed' && req.status !== 'cancelled' && (
                                <button
                                  onClick={() => openUpdateModal(req)}
                                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                  Update
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.total > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Showing {((pagination.page - 1) * pagination.items) + 1}–{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          disabled={pagination.page === 1}
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                        >
                          Prev
                        </button>
                        {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                          const pageNum = i + 1;
                          const totalPages = pagination.total_pages || 1;
                          const shouldShow = pageNum === 1 || pageNum === totalPages || (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                          if (!shouldShow) {
                            if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                              return <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>;
                            }
                            return null;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          disabled={pagination.page >= (pagination.total_pages || 1)}
                          onClick={() => setPage(p => Math.min(pagination.total_pages || 1, p + 1))}
                          className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Update Status Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Update Deletion Request</h3>
            <p className="text-sm text-gray-500 mb-4">
              {selectedRequest.subscriber_name} · {selectedRequest.ref_code}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <p className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded border border-gray-200">
                {REASON_LABELS[selectedRequest.reason] || selectedRequest.reason}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Update Status</label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setNewStatus(opt.value)}
                    className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
                      newStatus === opt.value
                        ? `${STATUS_COLORS[opt.value]} border-transparent`
                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Add internal notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {updateError && <p className="text-red-600 text-sm mb-3">{updateError}</p>}

            <div className="flex gap-3 justify-end">
              <button
                onClick={closeUpdateModal}
                disabled={updating}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={updating || !newStatus}
                className={`px-4 py-2 text-sm text-white rounded-md ${updating ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDeletionRequestsList;
